import { useCallback } from "react";
import useToaster from "./useToaster";

interface ApiResponseHandlerOptions {
  setErrors: React.Dispatch<React.SetStateAction<string[]>>;
}

export const useApiResponseHandler = ({
  setErrors,
}: ApiResponseHandlerOptions) => {
  const { showSuccessToast, showErrorToast } = useToaster();

  const handleApiError = useCallback(
    (error: any, defaultMessage: string) => {
      let errorMessage = defaultMessage;

      // Check for 429 status code in different error structures
      const statusCode =
        error?.response?.status || error?.status || error?.statusCode;

      // Handle 429 Too Many Requests specifically
      if (statusCode === 429) {
        errorMessage = "Please slow down. Take a chill and try again in a bit.";
        setErrors([errorMessage]);
        showErrorToast(errorMessage);
        return { message: errorMessage };
      }

      if (error?.response?.data) {
        const errorData = error.response.data;
        errorMessage = errorData.message || defaultMessage;
        if (errorData.errors && errorData.errors.length > 0) {
          setErrors(errorData.errors);
        } else if (errorData.message) {
          setErrors([errorData.message]);
        }
      } else if (error?.message) {
        setErrors([error.message]);
      } else {
        setErrors([defaultMessage]);
      }

      showErrorToast(errorMessage);
      return { message: errorMessage };
    },
    [setErrors, showErrorToast],
  );

  const handleApiSuccess = useCallback(
    (message: string) => {
      showSuccessToast(message);
    },
    [showSuccessToast],
  );

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, [setErrors]);

  return {
    handleApiError,
    handleApiSuccess,
    clearErrors,
  };
};
